import type { Key9Puzzle } from '../types/puzzle';

export type FullSudokuPlacement = {
  board: string[];
  changed: boolean;
  completed: boolean;
};

export type FullNotes = string[];

export type FullNoteChange = {
  notes: FullNotes;
  changed: boolean;
};

export type FullEraseChange = {
  board: string[];
  notes: FullNotes;
  changed: boolean;
};

export type FullSudokuReset = {
  board: string[];
  notes: FullNotes;
  fullHints: number;
  undoDepth: number;
  completed: false;
};

export type FullHintChange = {
  board: string[];
  notes: FullNotes;
  changed: boolean;
  cellIndex: number;
  previous: string;
  next: string;
  fullHints: number;
  completed: boolean;
  prompt: 'Select an empty cell for a hint.' | 'Hint filled this cell.';
};

export type FullNumberTapInput = {
  board: string[];
  notes: FullNotes;
  lockedCells: Set<number>;
  selectedCellIndex: number | null;
  digit: number;
  notesMode: boolean;
};

export type FullNumberTapResult =
  | (FullSudokuPlacement & { kind: 'placed'; notes: FullNotes })
  | (FullNoteChange & { kind: 'noted'; board: string[]; completed: boolean })
  | {
      kind: 'needs_cell' | 'ignored';
      prompt: 'Select an empty cell first.';
      board: string[];
      notes: FullNotes;
      changed: false;
      completed: boolean;
    };

export type FullSudokuConflictState = {
  conflictingUserCells: number[];
};

export function createFullBoard(puzzle: Key9Puzzle, lockedCells: Map<number, number>): string[] {
  const board = puzzle.puzzle.split('');
  lockedCells.forEach((digit, cellIndex) => {
    board[cellIndex] = String(digit);
  });
  return board;
}

export function createFullNotes(savedNotes?: string[]): FullNotes {
  const notes = Array.from({ length: 81 }, (_, index) => sanitizeNoteString(savedNotes?.[index] ?? ''));
  return notes;
}

export function resetFullSudokuProgress(puzzle: Key9Puzzle, lockedCells: Map<number, number>): FullSudokuReset {
  return {
    board: createFullBoard(puzzle, lockedCells),
    notes: createFullNotes(),
    fullHints: 0,
    undoDepth: 0,
    completed: false
  };
}

export function resolveFullNumberTap(puzzle: Key9Puzzle, input: FullNumberTapInput): FullNumberTapResult {
  const selectedCellIndex = input.selectedCellIndex;
  const base = {
    board: [...input.board],
    notes: createFullNotes(input.notes),
    changed: false as const,
    completed: isFullSudokuComplete(puzzle, input.board),
    prompt: 'Select an empty cell first.' as const
  };

  if (!isCellIndex(selectedCellIndex) || !canEditFullValueCell(puzzle, input.lockedCells, selectedCellIndex)) {
    return {
      kind: 'needs_cell',
      ...base
    };
  }

  if (input.notesMode) {
    const result = toggleFullNote(puzzle, input.board, input.lockedCells, input.notes, selectedCellIndex, input.digit);
    if (!result.changed) {
      return {
        kind: 'ignored',
        board: [...input.board],
        notes: result.notes,
        changed: false,
        completed: isFullSudokuComplete(puzzle, input.board),
        prompt: 'Select an empty cell first.'
      };
    }

    return {
      kind: 'noted',
      board: [...input.board],
      notes: result.notes,
      changed: true,
      completed: isFullSudokuComplete(puzzle, input.board),
    };
  }

  const result = placeFullDigit(puzzle, input.board, input.lockedCells, selectedCellIndex, input.digit);
  if (!result.changed) {
    return {
      kind: 'ignored',
      board: result.board,
      notes: createFullNotes(input.notes),
      changed: false,
      completed: result.completed,
      prompt: 'Select an empty cell first.'
    };
  }

  return {
    kind: 'placed',
    board: result.board,
    notes: clearFullNotesForCell(input.notes, selectedCellIndex),
    changed: true,
    completed: result.completed
  };
}

export function placeFullDigit(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  cellIndex: number,
  digit: number
): FullSudokuPlacement {
  if (lockedCells.has(cellIndex) || puzzle.puzzle[cellIndex] !== '0') {
    return {
      board: [...board],
      changed: false,
      completed: isFullSudokuComplete(puzzle, board)
    };
  }

  const nextBoard = [...board];
  nextBoard[cellIndex] = digit === 0 ? '0' : String(digit);

  return {
    board: nextBoard,
    changed: nextBoard[cellIndex] !== board[cellIndex],
    completed: isFullSudokuComplete(puzzle, nextBoard)
  };
}

export function toggleFullNote(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  notes: FullNotes,
  cellIndex: number,
  digit: number
): FullNoteChange {
  if (!canEditFullCell(puzzle, board, lockedCells, cellIndex) || digit < 1 || digit > 9) {
    return { notes: [...notes], changed: false };
  }

  const nextNotes = createFullNotes(notes);
  const current = new Set(nextNotes[cellIndex].split('').filter(Boolean));
  const key = String(digit);
  if (current.has(key)) {
    current.delete(key);
  } else {
    current.add(key);
  }
  nextNotes[cellIndex] = [...current].sort().join('');
  return {
    notes: nextNotes,
    changed: nextNotes[cellIndex] !== notes[cellIndex]
  };
}

export function clearFullNotesForCell(notes: FullNotes, cellIndex: number): FullNotes {
  const nextNotes = createFullNotes(notes);
  nextNotes[cellIndex] = '';
  return nextNotes;
}

export function eraseFullCellEntry(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  notes: FullNotes,
  cellIndex: number
): FullEraseChange {
  if (lockedCells.has(cellIndex) || puzzle.puzzle[cellIndex] !== '0') {
    return {
      board: [...board],
      notes: createFullNotes(notes),
      changed: false
    };
  }

  const nextBoard = [...board];
  const nextNotes = createFullNotes(notes);
  const changed = nextBoard[cellIndex] !== '0' || nextNotes[cellIndex] !== '';
  nextBoard[cellIndex] = '0';
  nextNotes[cellIndex] = '';
  return { board: nextBoard, notes: nextNotes, changed };
}

export function applyFullHint(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  notes: FullNotes,
  selectedCellIndex: number | null,
  fullHints: number
): FullHintChange {
  if (!canUseFullHint(puzzle, board, lockedCells, selectedCellIndex)) {
    return {
      board: [...board],
      notes: createFullNotes(notes),
      changed: false,
      cellIndex: -1,
      previous: '',
      next: '',
      fullHints,
      completed: isFullSudokuComplete(puzzle, board),
      prompt: 'Select an empty cell for a hint.'
    };
  }

  const cellIndex = selectedCellIndex;
  const nextBoard = [...board];
  const nextNotes = clearFullNotesForCell(notes, cellIndex);
  const previous = nextBoard[cellIndex] ?? '0';
  const next = puzzle.solution[cellIndex];
  nextBoard[cellIndex] = next;

  return {
    board: nextBoard,
    notes: nextNotes,
    changed: true,
    cellIndex,
    previous,
    next,
    fullHints: Math.max(0, Math.floor(fullHints)) + 1,
    completed: isFullSudokuComplete(puzzle, nextBoard),
    prompt: 'Hint filled this cell.'
  };
}

export function canUseFullHint(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>,
  selectedCellIndex: number | null
): selectedCellIndex is number {
  return isCellIndex(selectedCellIndex) && canHintFullCell(puzzle, board, lockedCells, selectedCellIndex);
}

export function isFullSudokuComplete(puzzle: Key9Puzzle, board: string[]): boolean {
  return board.join('') === puzzle.solution;
}

export function getFullSudokuConflicts(
  puzzle: Key9Puzzle,
  board: string[],
  lockedCells: Set<number>
): FullSudokuConflictState {
  const conflictCells = new Set<number>();
  const units = getAllUnits();

  for (const unit of units) {
    const byDigit = new Map<string, number[]>();
    for (const cell of unit) {
      const value = board[cell] ?? '0';
      if (!/^[1-9]$/.test(value)) continue;
      byDigit.set(value, [...(byDigit.get(value) ?? []), cell]);
    }

    byDigit.forEach((cells) => {
      if (cells.length < 2) return;
      cells
        .filter((cell) => !isFixedFullCell(puzzle, lockedCells, cell))
        .forEach((cell) => conflictCells.add(cell));
    });
  }

  return {
    conflictingUserCells: [...conflictCells].sort((a, b) => a - b)
  };
}

function canEditFullCell(puzzle: Key9Puzzle, board: string[], lockedCells: Set<number>, cellIndex: number): boolean {
  return !lockedCells.has(cellIndex) && puzzle.puzzle[cellIndex] === '0' && board[cellIndex] === '0';
}

function canEditFullValueCell(puzzle: Key9Puzzle, lockedCells: Set<number>, cellIndex: number): boolean {
  return !lockedCells.has(cellIndex) && puzzle.puzzle[cellIndex] === '0';
}

function canHintFullCell(puzzle: Key9Puzzle, board: string[], lockedCells: Set<number>, cellIndex: number): boolean {
  return !lockedCells.has(cellIndex) && puzzle.puzzle[cellIndex] === '0' && board[cellIndex] === '0';
}

function isFixedFullCell(puzzle: Key9Puzzle, lockedCells: Set<number>, cellIndex: number): boolean {
  return lockedCells.has(cellIndex) || puzzle.puzzle[cellIndex] !== '0';
}

function getAllUnits(): number[][] {
  const rows = Array.from({ length: 9 }, (_, row) => Array.from({ length: 9 }, (_, col) => row * 9 + col));
  const columns = Array.from({ length: 9 }, (_, col) => Array.from({ length: 9 }, (_, row) => row * 9 + col));
  const boxes = Array.from({ length: 9 }, (_, box) => {
    const boxRow = Math.floor(box / 3) * 3;
    const boxCol = (box % 3) * 3;
    return Array.from({ length: 9 }, (_, index) => {
      const row = boxRow + Math.floor(index / 3);
      const col = boxCol + (index % 3);
      return row * 9 + col;
    });
  });

  return [...rows, ...columns, ...boxes];
}

function isCellIndex(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < 81;
}

function sanitizeNoteString(value: string): string {
  return [...new Set(value.split('').filter((char) => /^[1-9]$/.test(char)))].sort().join('');
}
